<template>
  <div class="video-tooltip-overlay" v-if="movementId" :style="positionStyle">
    <div class="video-tooltip-content">
      <div class="video-tooltip-header">
        <h3>{{ name }}</h3>
      </div>

      <!-- Animation container with loading state -->
      <div v-if="hasAnimation" class="video-tooltip-animation-wrapper">
        <!-- Loading spinner -->
        <div v-if="isLoadingAnimation" class="animation-loading">
          <q-spinner color="terziary" size="40px" class="loading-spinner" />
        </div>

        <!-- Lottie animation container -->
        <div v-show="!isLoadingAnimation" ref="animationContainer" class="video-tooltip-animation-container"></div>
      </div>

      <p class="video-tooltip-description">{{ description }}</p>
    </div>
  </div>
</template>

<script>
import lottie from 'lottie-web';
import animationCache from '@/utils/services/animationCache';

export default {
  name: 'VideoTooltip',
  props: {
    movementId: {
      type: String,
      default: null
    },
    name: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    positionStyle: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      animation: null,
      isLoadingAnimation: false
    };
  },
  computed: {
    hasAnimation() {
      return this.movementId && animationCache.getAnimationUrls()[this.movementId];
    }
  },
  watch: {
    movementId(newValue) {
      // Destroy previous animation if any
      if (this.animation) {
        this.animation.destroy();
        this.animation = null;
      }

      // If we have a new movementId and it has an animation, load it
      if (newValue && this.hasAnimation) {
        this.$nextTick(() => {
          this.loadAnimation();
        });
      }
    }
  },
  methods: {
    async loadAnimation() {
      if (!this.$refs.animationContainer || !this.movementId) return;

      try {
        // Check if animation is already cached
        const cachedData = animationCache.getCachedAnimation(this.movementId);

        if (cachedData) {
          // Use cached animation data immediately
          this.loadAnimationFromData(cachedData);
          return;
        }

        // Check if animation is currently loading
        if (animationCache.isAnimationLoading(this.movementId)) {
          this.isLoadingAnimation = true;
        }

        // Load animation from cache service (will fetch if not cached)
        this.isLoadingAnimation = true;
        const animationData = await animationCache.loadAnimation(this.movementId);

        if (animationData) {
          this.loadAnimationFromData(animationData);
        } else {
          console.warn(`Failed to load animation: ${this.movementId}`);
        }
      } catch (error) {
        console.error(`Error loading animation for ${this.movementId}:`, error);
      } finally {
        this.isLoadingAnimation = false;
      }
    },

    loadAnimationFromData(animationData) {
      if (!this.$refs.animationContainer) return;

      try {
        this.animation = lottie.loadAnimation({
          container: this.$refs.animationContainer,
          renderer: 'svg',
          loop: true,
          autoplay: true,
          animationData: animationData, // Use preloaded data
          rendererSettings: {
            preserveAspectRatio: 'xMidYMid slice'
          }
        });

        // Add background styling to animation container
        this.$refs.animationContainer.style.backgroundColor = '#f9f9f9';
        this.$refs.animationContainer.style.borderRadius = '8px';
      } catch (error) {
        console.error(`Error rendering animation ${this.movementId}:`, error);
      }
    }
  },
  mounted() {
    if (this.hasAnimation) {
      this.loadAnimation();
    }
  },
  beforeDestroy() {
    if (this.animation) {
      this.animation.destroy();
      this.animation = null;
    }
  }
};
</script>

<style lang="scss" scoped>
.video-tooltip-overlay {
  position: fixed;
  z-index: 10005;
  pointer-events: none;
  width: auto;
  height: auto;
}

.video-tooltip-content {
  background: white;
  border-radius: 12px;
  padding: 20px;
  min-width: 280px;
  max-width: 350px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 12px;
  backdrop-filter: blur(10px);
}

.video-tooltip-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;

  h3 {
    @include mona-sans-font($size: 20px, $bolded: true, $color: var(--main-text-color));
    margin: 0;
  }
}

.video-tooltip-description {
  @include inter-font($size: 14px, $color: var(--secondary-text-color));
  margin: 0;
  line-height: 1.6;
  font-weight: 500;
}

.video-tooltip-animation-wrapper {
  position: relative;
  margin: 4px 0;
  width: 100%;
  height: 200px;
  border-radius: 8px;
  overflow: hidden;
}

.video-tooltip-animation-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: transparent;
  border-radius: 8px;
}

.animation-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f9f9f9;
  border-radius: 8px;
  z-index: 1;
}

.loading-spinner {
  margin-bottom: 12px;
}

.loading-text {
  @include inter-font($size: 12px, $color: var(--secondary-text-color));
  margin: 0;
  font-weight: 500;
}
</style>
